import React, { useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { useSendoutAccountability } from 'features/sendouts/hooks';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { EntityRoutes } from 'routes/constants';
import { Permissions, userHasPermission } from 'services/Authorization';
import strings from 'strings';
import { FPActionButton, FPIconButton, When } from 'UI/components/atoms';
import FPChip from 'UI/components/atoms/FPChip';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { SendoutStatus, UIStatus } from 'UI/constants/status';

import { buildStatusReasonItems, DetailsSummary, StatusReasonsSummary } from '../../shared';
import { buildRelatedEntities } from '../../shared/DetailsSummary/DetailsSummary.utils';

import { REASONS_TITLE_BY_STATUS } from './SendoutDetailsDrawer.constants';
import {
  Header,
  HeaderTitle,
  HeaderTitleContainer,
  LoaderContainer,
  useStyles
} from './SendoutDetailsDrawer.styled';
import { SendoutDetailsTabs } from './SendoutDetailsTabs';

const SENDOUT_DETAILS_COPIES = strings.sendoutsV2.drawers.sendoutDetails;

export const SendoutDetailsDrawer = ({
  onClose = () => {},
  onEditDetails = () => {},
  onEditInterview = () => {},
  onScheduleInterview = () => {},
  onStartPlacement = () => {},
  onViewInterviewDetails = () => {},
  sendoutId
}) => {
  const classes = useStyles();

  const [isPlacementConfirmOpen, setIsPlacementConfirmOpen] = useState(false);

  const { state, refreshData } = useFetchWithStatusV2({
    endpoint: sendoutId ? `${Endpoints.Sendouts}/${sendoutId}?` : null,
    apiVersion: 1
  });

  const { canEditSendout: canManageSendout, canStartPlacement: isPlacementAccountable } =
    useSendoutAccountability(state.results);

  const isFetching = state.status === UIStatus.Loading;

  const sendoutStatus = state.results?.status || {};
  const canStartPlacement =
    isPlacementAccountable &&
    userHasPermission(Permissions.Placements.CanUseModule) &&
    sendoutStatus.id === SendoutStatus.Active;
  const declinationReasons = buildStatusReasonItems(state.results?.declination_details);

  const moreActionsDefinition = [
    {
      title: SENDOUT_DETAILS_COPIES.actions.editDetails,
      visible: canManageSendout,
      action: () => onEditDetails(refreshData)
    },
    {
      title: SENDOUT_DETAILS_COPIES.actions.closingCall,
      visible: false,
      action: () => console.log('Closing Call') // TODO: Implement closing call action
    }
  ];

  const relatedEntities = buildRelatedEntities(state.results);
  const jobOrderId = state.results?.joborder?.id;
  const jobOrderProfileUrl = jobOrderId
    ? EntityRoutes.JobOrderProfile.replace(':id', jobOrderId)
    : null;

  return (
    <>
      <FPDrawer
        FPDrawerProps={{ open: true, classes: { paper: classes.drawerLarge } }}
        onClose={onClose}
        contentContainerClassName={classes.drawerContentContainer}
        header={
          <Header>
            <HeaderTitleContainer>
              <HeaderTitle variant="h5">{SENDOUT_DETAILS_COPIES.title}</HeaderTitle>
              <FPChip
                size="small"
                aria-label="sendout status chip"
                label={sendoutStatus.title}
                variant="outlined"
                color={sendoutStatus.color}
              />
            </HeaderTitleContainer>
            <FPIconButton
              data-testid={strings.shared.ui.close}
              icon={HighlightOffIcon}
              onClick={onClose}
              tooltipProps={{ title: strings.shared.ui.close }}
            />
          </Header>
        }
      >
        <When condition={state.status === UIStatus.Error}>
          <EmptyPlaceholder title={strings.sendoutsV2.drawers.tabs.status.error} />
        </When>
        <When condition={isFetching}>
          <LoaderContainer>
            <CircularProgress />
          </LoaderContainer>
        </When>
        <When condition={state.status === UIStatus.Success}>
          <div className={classes.drawerSection}>
            <DetailsSummary
              title={state.results?.joborder?.title}
              titleLinkProps={
                jobOrderProfileUrl
                  ? {
                      url: jobOrderProfileUrl,
                      target: '_blank'
                    }
                  : null
              }
              primaryAction={
                <When condition={canStartPlacement}>
                  <FPActionButton
                    text={SENDOUT_DETAILS_COPIES.actions.startPlacement}
                    onClick={() => setIsPlacementConfirmOpen(true)}
                  />
                </When>
              }
              moreActionsDefinition={moreActionsDefinition}
              relatedEntities={relatedEntities}
            />
            <StatusReasonsSummary
              title={REASONS_TITLE_BY_STATUS[sendoutStatus.id]}
              items={declinationReasons}
            />
          </div>
          <SendoutDetailsTabs
            canManageSendout={canManageSendout}
            onEditInterview={onEditInterview}
            onScheduleInterview={onScheduleInterview}
            onViewInterviewDetails={onViewInterviewDetails}
            sendoutId={sendoutId}
          />
        </When>
      </FPDrawer>
      <When condition={isPlacementConfirmOpen}>
        <DecisionDialog
          isOpened
          severity="info"
          title={strings.shared.ui.confirm}
          message={strings.sendouts.details.navigationPlacementConfirm}
          confirmButtonText={strings.shared.ui.singleConfirm}
          cancelButtonText={strings.shared.ui.cancel}
          withButtons="YesNo"
          showSeverityIcon
          showCloseButton={false}
          onClose={() => setIsPlacementConfirmOpen(false)}
          onConfirm={() => {
            setIsPlacementConfirmOpen(false);
            onStartPlacement();
          }}
        />
      </When>
    </>
  );
};
